<template>
  <template v-if="isLoaded">
      <div class="farm-window saves-card">
        <div class="stat-row">
          <h2 style="text-align: left;">Saves</h2>
          <p style="text-align: right;">Version {{ saveSystem.gameVersion }}</p>
        </div>
        <div class="save-selection-box">
          <div v-if="saveSystem.hasFarm"
               class="farm-window selected-save"
               :class="{'not-saved': !saveSystem.currentGameIsSaved, 'outdated-save': saveSystem.saveIsOutdated(farm.state.saveName)}">
            <input v-model="farm.state.saveName" style="grid-area: input;">
            <div style="grid-area: version; display: grid; grid-template-columns: 4fr 1fr;">
              <h3>
                <span v-if="saveSystem.saveIsOutdated(farm.state.saveName)">
                  OUTDATED <span class="unimportant">(saved {{ date(farm.state.saveDate) }})</span>
                </span>
                <span v-else-if="saveSystem.currentGameIsSaved">
                  Saved <span class="unimportant">({{ date(farm.state.saveDate) }})</span>
                </span>
                <span v-else>
                  Not saved <span class="unimportant">(since {{ date(farm.state.saveDate) }})</span>
                </span>
              </h3>
              <p style="margin-left: 1ch; text-align: right;">v{{ saveSystem.saveVersion(farm.state.saveName) }}</p>
            </div>
            <div class="save-info-detailed">
              <p><i>{{util().currentDate(farm.state.time)}}</i></p>
              <div class="save-import-cell" title="Import a previously exported save file. If this fails, there will be no error message, but an error should be visible if you open your browser console.">
                <label for="save-import" class="farm-button save-import-label"><h3>Import</h3></label>
                <input id="save-import" type="file" accept=".cow" v-on:change="saveSystem.importSave"
                       style="display: none;">
              </div>
              <TextButton @click="saveSystem.exportSave(); $forceUpdate()"
                          class="farm-button"
                          style="" title="Export this save game to file.">
                <h3>Export</h3>
              </TextButton>
            </div>
            <TextButton @click="saveSystem.saveGame(); $forceUpdate()"
                        v-if="!saveSystem.saveIsOutdated(farm.state.saveName)"
                        class="farm-button"
                        style="grid-area: save;">
              <h3>Save</h3>
            </TextButton>
            <TextButton @click="saveSystem.deleteGame(); $forceUpdate()"
                        class="farm-button delete-save-button"
                        style="grid-area: delete; padding: 0;">
              <h3>Delete</h3>
            </TextButton>
          </div>
        </div>

        <div class="farm-list saves-list">
          <div v-for="(save, saveName) in saveSystem.allSaves" :key="saveName"
               class="save-entry" :class="{'outdated-save': saveSystem.saveIsOutdated(saveName)}">
            <div class="save-info">
              <h3><span v-if="saveSystem.saveIsOutdated(saveName)">OUTDATED: </span>{{ saveName }}</h3>
              <p style="text-align: right; margin-right: 10px;">v{{ save.gameVersion }}</p>
              <p><i>{{util().currentDate(save.time)}}</i></p>
            </div>
            <TextButton @click="saveSystem.loadGame(saveName); $forceUpdate()"
                        class="load-button farm-button">
              <h3>Load</h3>
            </TextButton>
          </div>
          <TextButton @click="saveSystem.newGame(); $forceUpdate()"
                      class="farm-button new-game-button save-entry">
            <div class="new-game-button-internals">
              <h3>New Game</h3>
              <p style="font-size: 0.8em;">(Save current game first)</p>
            </div>
          </TextButton>
        </div>
      </div>
  </template>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import Farm from '@/farm/Farm'
import EmployeeCard from '@/components/EmployeeCard.vue'
import ValueMeter from '@/components/ui/ValueMeter.vue'
import util from '../util'
import FarmStatusCard from '@/components/FarmStatusCard.vue'
import StatusBar from '@/components/StatusBar.vue'
import TextButton from '@/components/ui/TextButton.vue'
import SaveSystem from '@/SaveSystem'

@Options({
  components: {
    TextButton
  },
  props: {
    saveSystem: Object
  },
  methods: {
    log(msg: any) {
      console.log(msg)
    },
    util() {
      return util
    },
    date(unixTime: number) {
      let date = new Date(unixTime)
      return date.toLocaleString('sv-SE', { hour12: false, year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    window() {
      return window
    }
  },
  computed: {
    farm: function (): Farm {
      return this.saveSystem.farm
    }
  },
  async created() {
    this.isLoaded = true
  }
})
export default class SavesCard extends Vue {
  isLoaded = false
  saveSystem!: SaveSystem
  selectedSaveName = ''
}
</script>

<style scoped lang="scss">
.saves-card {
  display: grid;
  grid-template-rows: 30px 160px 1fr;
  grid-template-areas:
    "t"
    "s"
    "l";
  grid-gap: 10px;

  box-sizing: border-box;

  input {
    background-color: $component-background-color;
    color: white;
    border: $component-border-style;
    text-align: center;
    font-size: 16px;
  }
}

.save-selection-box {
  display: grid;
  background-color: $component-background-color;
  min-height: 150px;
  border: $component-border-style;
}

.selected-save {
  display: grid;
  grid-template-columns: 3fr 1fr;
  grid-template-rows: 30px 1fr 1fr;
  grid-template-areas:
    "version delete"
    "input save"
    "date save";
  grid-gap: 10px;
  border: 0;

  .text-button {
    padding: 10px;
  }

  p {
    margin-top: auto;
    margin-bottom: auto;
  }

  .save-info-detailed {
    grid-area: date;
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    grid-gap: 10px;

    & > :nth-child(2) {
      text-align: right;
    }
  }

  .unimportant {
    font-weight: normal;
    font-size: 0.75em;
  }
}

.not-saved {
  background: scale-color($component-base-color, $lightness: 20%);
}

.no-selected-save {
  text-align: center;
  margin-top: auto;
  margin-bottom: auto;
}

.stat-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: minmax(40px, 1fr);
}

.text-button {
  border: $component-border-style;

  display: table;
  div {
    display: table-cell;
    vertical-align: middle;
  }

  & > h3 {
    display: table-cell;
    vertical-align: middle;
    text-align: center;
  }
}

.save-import-cell {
  position: relative;
}

.save-import-label {
  cursor: pointer;
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;

  border: 1px solid black;

  h3 {
    text-align: center;
    margin: 11px 5px 0 5px;
  }

}

.saves-list {
  overflow-y: auto;
}

.save-entry {
  background-color: $component-base-color;
  border: $component-border-style;
  display: grid;
  grid-template-columns: 3fr minmax(75px, 1fr);
  text-align: center;
  margin-bottom: 10px;
  min-height: 50px;

  .text-button {
    padding: 5px;
    border: 0;
    border-left: $component-border-style !important;
  }
}

.outdated-save {
  background: #333;
  color: #999;

  input {
    background: #111;
    color: #999;
  }
}

.save-info {
  display: grid;
  grid-template-columns: 1fr 50px;
  grid-template-rows: 1fr 1fr;
  min-width: 140px;
  margin-top: auto;
  margin-bottom: auto;
  line-height: 1.2;

  p {
    font-size: 0.8em;
  }
}

.new-game-button {
  grid-template-columns: 1fr;
  background-color: #005700;

  &:hover {
    background-color: scale-color(lighten(#005700, 10%), $saturation: -30%);
  }
  &:active {
    background-color: scale-color(darken(#005700, 5%), $saturation: -5%);
  }

  .new-game-button-internals {
    margin: auto;
    line-height: 1.2;
  }
}

.load-button {
  border-width: 0 !important;
  border-right-width: inherit !important;
  overflow-wrap: anywhere;
}

.delete-save-button {
  background-color: #8D141E;
  display: grid;

  &:hover {
    background-color: scale-color(lighten(#8D141E, 10%), $saturation: 30%);
  }
  &:active {
    background-color: scale-color(darken(#8D141E, 5%), $saturation: -5%);
  }

  h1, h2, h3, h4, p {
    margin-top: auto;
    margin-bottom: auto;
  }
}
</style>
